import {SearchType} from "@/lib/service/eidr-proxy-service";
import xml2js       from 'xml2js'
import CryptoJS     from "crypto-js";
import {auth}       from "@/auth";


export const getRegistryUri = async (url) => {
    let session = await auth();
    if (session?.user?.authorization && process.env.EIDR_AUTHENTICATED_API_URI) {
        return process.env.EIDR_AUTHENTICATED_API_URI + url;
    }
    return process.env.EIDR_API_URI + url;
}
/**
 * Generates a SHA-256 hash from the given password and encodes it in Base64 format.
 *
 * @function
 * @name generateSha256Hash
 * @param {string} password - The input string to hash.
 * @return {string} The Base64-encoded SHA-256 hash of the input password.
 */
export const generateSha256Hash = (password) => {
    const hashed = CryptoJS.SHA256(password);
    return CryptoJS.enc.Base64.stringify(hashed);
};


/**
 * A function that generates an authorization string for EIDR API requests.
 *
 * @function
 * @async
 * @param {string|null} [username=null] - The username for authentication. Defaults to null.
 * @param {string|null} [partyId=null] - The party ID for authentication. Defaults to null.
 * @param {string|null} [password=null] - The password for authentication. Defaults to null.
 * @return {string} Tthe constructed authorization string in the format:
 * "Eidr <username>:<partyId>:<hashedPassword>".
 */
export const getAuthorization = async (username = null, partyId = null, password = null) => {
    let session = await auth();
    if (session?.user?.authorization) {
        console.log("Using session for auth")
        return session.user.authorization
    }
    if (username && partyId && password) {
        console.log("Using username and partyId and password for auth")
        return `Eidr ${username}:${partyId}:${generateSha256Hash(password)}`;
    }
    console.log("Using env vars for auth")
    const hashedPassword = generateSha256Hash(process.env.EIDR_API_CREDS_PASSWORD);
    return `Eidr ${process.env.EIDR_API_CREDS_USERNAME}:${process.env.EIDR_API_CREDS_PARTYID}:${hashedPassword}`;
};

/**
 * Generates an XML string for searching parties by name.
 *
 * @param {Object} options - The options for generating the search XML.
 * @param {string} options.partyName - The name of the party to search for.
 * @param {boolean} [options.exactMatch=false] - Determines whether to match the party name exactly.
 *                                               If false, alternate names will be included.
 * @param {string} [options.activeFilter='active'] - Specifies the active filter for the search.
 *                                                   Defaults to 'active'. Can be empty for no filtering.
 * @param {boolean} [options.returnAll=false] - Indicates whether to return all results (currently unused).
 * @param {number} [options.pageNumber=1] - The page number to return in the search results.
 * @param {number} [options.pageSize=100] - The number of results to return per page.
 * @return {string}
 */
export const generatePartySearchXml = (
    {
        partyName,
        exactMatch = false,
        activeFilter = 'active',
        returnAll = false,
        pageNumber = 1,
        pageSize = 100
    }) => {

    //language=XML
    return `
        <FindPartiesByName xmlns="http://www.eidr.org/schema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                           ${exactMatch === false ? 'includeAlternateNames="true"' : ''}>
            <PartyName>${partyName}</PartyName>
            ${activeFilter !== '' ? `<ActiveFilter>${activeFilter}</ActiveFilter>` : ''}
            <PageNumber>${pageNumber}</PageNumber>
            <PageSize>${pageSize}</PageSize>
        </FindPartiesByName>`
}

/**
 * Generates a search query object for party-related searches with various filtering options.
 *
 * @param {string} partyName - The name of the party to search for.
 * @param {boolean} [exactMatch=false] - Specifies whether to perform an exact match search on the party name.
 * @param {string} [activeFilter='active'] - The filter to apply based on activity status. Usually 'active', 'inactive', or other valid statuses.
 * @param {boolean} [returnAll=false] - Whether to return all matching records without pagination.
 * @param {number} [pageNumber=1] - The page number for the search results, used in pagination.
 * @param {number} [pageSize=100] - The number of results to return per page.
 * @return {{partyName, exactMatch: boolean, activeFilter: string, pageNumber: number, pageSize: number}}
 */
export const generatePartySearchQuery = (
    partyName,
    exactMatch = false,
    activeFilter = 'active',
    returnAll = false,
    pageNumber = 1,
    pageSize = 100) => {
    return {
        partyName: partyName,
        exactMatch: exactMatch,
        activeFilter: activeFilter,
        pageNumber: pageNumber,
        pageSize: pageSize,
    }
}

/**
 * Generates an XML string for searching services by their name with optional parameters for filtering and pagination.
 *
 * @param {Object} options - An object containing parameters for generating the XML.
 * @param {string} options.serviceName - The name of the service to search for.
 * @param {boolean} [options.exactMatch=false] - Whether to perform an exact match search (parameter currently not utilized in the XML output).
 * @param {string} [options.status='active'] - The status filter for the search. Default is 'active'.
 * @param {boolean} [options.returnAll=false] - Indicates whether to return all search results (parameter currently not utilized in the XML output).
 * @param {number} [options.pageNumber=1] - The page number for paginated search results. Default is 1.
 * @param {number} [options.pageSize=100] - The number of results per page. Default is 100.
 * @return  {string}
 */
export const generateServiceSearchXml = (
    {
        serviceName,
        exactMatch = false,
        status = 'active',
        returnAll = false,
        pageNumber = 1,
        pageSize = 100
    }) => {
    //language=XML
    return `
        <FindServicesByName xmlns="http://www.eidr.org/schema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <ServiceName>${serviceName}</ServiceName>
            <ActiveFilter>${status}</ActiveFilter>
            <PageNumber>${pageNumber}</PageNumber>
            <PageSize>${pageSize}</PageSize>
        </FindServicesByName>`
}

/**
 * Generates a search query object for retrieving services based on specified parameters.
 *
 * @param {string} serviceName - The name of the service to search for.
 * @param {boolean} [startsWith=false] - Whether the search should match services starting with the specified name.
 * @param {string} [status='active'] - The activity status to filter services by (e.g., 'active', 'inactive').
 * @param {boolean} [returnAll=false] - Whether to return all matching results without pagination.
 * @param {number} [pageNumber=1] - The page number to retrieve when pagination is applied.
 * @param {number} [pageSize=100] - The number of results per page when pagination is applied.
 * @return {{serviceName, startsWith: boolean, activeFilter: string, pageNumber: number, pageSize: number}}
 */
export const generateServiceSearchQuery = (
    serviceName,
    startsWith = false,
    status = 'active',
    returnAll = false,
    pageNumber = 1,
    pageSize = 100) => {
    return {
        serviceName: serviceName,
        startsWith: startsWith,
        activeFilter: status,
        pageNumber: pageNumber,
        pageSize: pageSize,
    }
}

export const partyResolve = async (id) => {
    let url = await getRegistryUri(`/party/resolve/${id}`);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: await getAuthorization(),
        },
        cache: 'force-cache',
        next: {revalidate: 24 * 60 * 1000}
    });
    let xml = await response.text();

    //console.log(xml);
    xml = xml.replaceAll("md:", "");
    let json = await xml2js.parseStringPromise(xml, {
        trim: true,
        explicitArray: false,
        stripPrefix: true,
        parseNumbers: true,
        parseBooleans: true,
        attrkey: '$',
        charkey: '_',
        explicitRoot: false,
        explicitChildren: true,
        ignoreAttrs: true,
        mergeAttrs: true,

    })

    return json
}

/**
 * Asynchronously performs a party search query to the EIDR API and processes the response.
 *
 * This function sends a POST request with a specified query to the EIDR API to retrieve
 * party data based on the provided search type. The response is processed and returned in a
 * JSON format, depending on the type of search specified.
 *
 * @param {string} query - The query string containing the search criteria.
 * @param {string} [searchType=SearchType.Full] - The type of search to perform, defaults to full search.
 *        `SearchType.Full` retrieves comprehensive details,
 *        while other types return a summarized list of parties.
 * @return {Promise<import("../types/common-types").RequestResponseArray>}
 *
 */
export const partySearch = async (query, searchType = SearchType.Full) => {
    let url = await getRegistryUri(`/party/query?type=${searchType}`);

    try {

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: await getAuthorization(),
            },
            body: query,
        });
        let xml = await response.text();
        xml = xml.replaceAll("md:", "");

        let json = await xml2js.parseStringPromise(xml, {
            trim: true,
            explicitArray: false,
        })


        if (SearchType.Full === searchType) {
            json = postProcessPartyJson(json.PartyQueryResults);
        } else {
            json = json.PartyQueryResults
            if (json.PartyID && !Array.isArray(json.PartyID)) {
                json.PartyID = [json.PartyID]
            } else if (!json.PartyID) {
                json.PartyID = []
            }
            json = {
                totalMatches: parseInt(json.TotalMatches),
                pageNumber: parseInt(json.PageNumber),
                pageSize: parseInt(json.CurrentSize),
                results: json.PartyID.map((partyID) => {
                    return {
                        ID: partyID
                    }
                })
            }
        }

        return {
            response: json, request: {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "...."
                },
                body: query,
                xml: xml
            }
        };
    } catch (error) {
        console.log(error);
        return {
            response: {error: error.message}, request: {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "...."
                },
                body: query,
            }
        }
    }
}

/**
 * Fetches and retrieves all parties through paginated search.
 *
 * This asynchronous function performs a paginated search to retrieve all parties
 * based on the specified criteria, combining results from each page into one
 * consolidated list of parties.
 *
 * It initializes search parameters including `partyName`, `exactMatch`,
 * `activeFilter`, `returnAll`, `pageNumber`, and `pageSize`. The search query
 * is generated as XML and sent via the `partySearch` function. The function
 * continues to fetch additional pages of results until all parties are retrieved.
 *
 * @async
 * @function getAllParties
 * @return {Promise<{totalMatches: number, pageNumber: number, pageSize: number, results: {ID: *}[]}|{totalMatches: number, pageNumber: number, pageSize: number, results: {PartyName: {}}[]}>}
 * the consolidated list of party results and related information.
 */
export const getAllParties = async () => {
    let obj = {
        partyName: '',
        exactMatch: false,
        activeFilter: 'active',
        returnAll: true,
        pageNumber: 1,
        pageSize: 2500
    }
    let query = generatePartySearchXml(obj)
    let count = 0
    let {response, request} = await partySearch(query, SearchType.Full)
    let firstResponse = response;
    while (count < (firstResponse.totalMatches ?? 0)) {
        count += firstResponse.pageSize
        obj.pageNumber += 1
        let {response, request} = await partySearch(generatePartySearchXml(obj), SearchType.Full)
        firstResponse.results = firstResponse.results.concat(response.results)
    }
    return firstResponse
}

/**
 * Asynchronously performs a service search query by sending a POST request to the EIDR API.
 *
 * This function takes in an XPath query, the type of search to perform, and an optional query object
 * for further filtering of the results. It processes the XML response into JSON format and structures
 * it before returning it as a response object.
 *
 * @param {string} xpath - The XPath query used to filter search results.
 * @param {string} [searchType=SearchType.Full] - The type of search to execute (default is `SearchType.Full`).
 * @param {Object} [query] - Additional query object for filtering results.
 * @param {string} [query.serviceName] - A name filter to match services by their name or alternate names.
 * @param {boolean} [query.startWith] - A name filter to match only services that start with specific words
 *
 * @return {Promise<import("../types/common-types").RequestResponseArray>}
 *
 */
export const serviceSearch = async (xpath, searchType = SearchType.Full, query) => {
    console.log(query, searchType);
    let url = await getRegistryUri(`/service/query?type=${searchType}`);

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: await getAuthorization(),
            },
            body: xpath,
            cache: 'force-cache',
            next: {revalidate: 24 * 60 * 1000}
        });
        let xml = await response.text();

        //console.log(xml);
        xml = xml.replaceAll("md:", "");
        let json = await xml2js.parseStringPromise(xml, {
            trim: true,
            explicitArray: false,
            stripPrefix: true,
            parseNumbers: true,
            parseBooleans: true,
            attrkey: '$',
            charkey: '_',
            explicitRoot: false,
            explicitChildren: true,
            ignoreAttrs: true,
            mergeAttrs: true,

        })

        if (json.Service && !Array.isArray(json.Service)) {
            json.Service = [json.Service]
        } else if (!json.Service) {
            json.Service = []
        }


        if (query && query?.startsWith) {

            json.Service = json.Service.filter(service => {
                if (service.ServiceName.DisplayName.toLowerCase().startsWith(query.serviceName.toLowerCase())) {
                    return true
                }
                if (Array.isArray(service.AlternateServiceName)) {
                    return service.AlternateServiceName.some(name => {
                        return name.startsWith(query.serviceName)
                    })
                } else if (typeof service.AlternateServiceName === "string") {
                    return service.AlternateServiceName.toLowerCase().startsWith(query.serviceName.toLowerCase())
                }


                return false;
            })
            json.TotalMatches = json.Service.length;
        }


        //json = postProcessServiceJson(json.ServiceQueryResults);

        let obj = {
            totalMatches: parseInt(json.TotalMatches),
            pageNumber: parseInt(json.PageNumber),
            pageSize: parseInt(json.CurrentSize),
            results: Array.isArray(json.Service) ? json.Service : [json.Service],
        }

        return {
            response: obj, request: {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "...",
                },
                body: query,
            }
        };
    } catch (error) {
        console.log(error);
        return {
            response: {error: error.message}, request: {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "...",
                },
            }
        }
    }
}

/**
 * Retrieves all services by performing a paginated search with specific filtering and configuration.
 * This function generates a search query based on predefined criteria, executes the search,
 * and aggregates results from multiple pages until all matching services are retrieved.
 *
 * @async
 * @function getAllServices
 * @return {Promise<{totalMatches: number, pageNumber: number, pageSize: number, results: (*[]|*|((*|[])[]|[]|*)[])}>} A Promise that resolves to an object containing the aggregated search results,
 * including all service details, total matches, and other metadata from the search.
 *
 * The structure and content of the returned object depend on the response schema of the `serviceSearch` function.
 */
export const getAllServices = async () => {
    let obj = {
        serviceName: '',
        exactMatch: false,
        activeFilter: 'active',
        returnAll: true,
        pageNumber: 1,
        pageSize: 2500
    }
    let query = generateServiceSearchXml(obj)
    let count = 0
    let {response, request} = await serviceSearch(query, SearchType.Full, {})

    let firstResponse = response;
    while (count < firstResponse.totalMatches) {
        count += firstResponse.pageSize
        obj.pageNumber += 1
        let {response, request} = await serviceSearch(generateServiceSearchXml(obj), SearchType.Full, {})
        firstResponse.results = firstResponse.results.concat(response.results)
    }
    return firstResponse
}

/**
 * Resolves user details by making an asynchronous request to the specified API endpoint.
 * Retrieves and parses the response in XML format, converts it to JSON, and returns the result
 * along with the details of the initial request.
 *
 * @param {string} userId - The unique identifier of the user to be resolved.
 * @param {string|null} [partyId=null] - An optional parameter for the party ID to include in the authorization process.
 * @param {string|null} [password=null] - An optional password for user authorization.
 * @param {string} [searchType=SearchType.Full] - The type of search to perform. Defaults to a full search.
 * @return {Promise<import("../types/common-types").RequestUserResponse>}
 */
export const userResolve = async (userId, partyId = null, password = null, searchType = SearchType.Full) => {
    let url = await getRegistryUri(`/user/resolve/${userId}?type=${searchType}`);
    console.log(url);
    try {
        let payload = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: await getAuthorization(userId, partyId, password),
            },
            cache: 'force-cache',
            next: {revalidate: 24 * 60 * 1000}
        }
        const response = await fetch(url, payload);

        let xml = await response.text();

        console.log(xml, payload);

        xml = xml.replaceAll("md:", "");
        let json = await xml2js.parseStringPromise(xml, {
            trim: true,
            explicitArray: false,
            stripPrefix: true,
            parseNumbers: true,
            parseBooleans: true,
            attrkey: '$',
            charkey: '_',
            explicitRoot: false,
            explicitChildren: true,
            ignoreAttrs: true,
            mergeAttrs: true,

        })

        json.Party = await partyResolve(json.ParentParty);

        if (json?.Party?.AllowedRoles) {
            if (!Array.isArray(json.Party.AllowedRoles)) {
                json.Party.AllowedRoles = [json.Party.AllowedRoles]
            }
        }

        return {
            response: json, request: {
                url: url,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "...",
                },
            }
        }
    } catch (error) {
        console.log(error);
        return {
            response: {error: error.message}, request: {
                url: url,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: "...",
                },
            }
        }
    }
}

/**
 * Processes and transforms a JSON object containing party information into a standardized structure.
 *
 * The function ensures that the `Party` property in the JSON object is always an array. If the `Party` property
 * is a single object, it wraps it in an array. If it doesn't exist, it initializes it as an empty array. Each party
 * object within the `Party` array is then transformed to include a `PartyName` object, which is further processed
 * to separate organization identifiers and other information into distinct properties. Additional top-level
 * metadata (e.g., `totalMatches`, `pageNumber`, `pageSize`) is also parsed and included in the transformed output.
 *
 * @param {Object} json - The input JSON object containing party data and metadata.
 * @param {Array|Object} json.Party - Represents parties as an array or a single object.
 * @param {string} json.TotalMatches - Total number of matches as a string.
 * @param {string} json.PageNumber - Current page number as a string.
 * @param {string} json.CurrentSize - Current page size as a string.
 * @return {object} The transformed object containing standardized party data and metadata.

 */
export const postProcessPartyJson = (json) => {
    if (json.Party && !Array.isArray(json.Party)) {
        json.Party = [json.Party]
    } else if (!json.Party) {
        json.Party = []
    }
    let results = json.Party.map(party => {
        let obj = {PartyName: {},}
        for (let [key, value] of Object.entries(party)) {
            switch (key) {
                case "PartyName":
                    obj.PartyName = obj.PartyName || {}
                    for (let [k, v] of Object.entries(value)) {
                        switch (k) {
                            case "$" :
                                obj.PartyName.organizationID = Object.values(v);
                                break;
                            default :
                                obj.PartyName[k] = v;
                        }
                    }
                    break;
                default :
                    obj[key] = value;
            }
        }
        return obj;
    })
    return {
        totalMatches: parseInt(json.TotalMatches),
        pageNumber: parseInt(json.PageNumber),
        pageSize: parseInt(json.CurrentSize),
        results: results,
    };
}

export const postProcessServiceJson = (json) => {
    if (json.Service && !Array.isArray(json.Service)) {
        json.Service = [json.Service]
    } else if (!json.Service) {
        json.Service = []
    }
    let results = json.Service.map(service => {
        let obj = {AlternateServiceName: [], OtherAffiliation: []}
        for (let [key, value] of Object.entries(service)) {
            console.log("outer", key, value);
            switch (key) {
                case "AlternateServiceName":
                    obj.AlternateServiceName = obj.AlternateServiceName || []
                    if (!Array.isArray(value)) {
                        value = [value]
                    }
                    console.log("inner", value);
                    let item = {}

                    value.forEach((val) => {
                        if (typeof val === "string") {

                        }
                        for (let [k, v] of Object.entries(val)) {
                            console.log("innner 2", k, v);
                            switch (k) {
                                case "_" :
                                    item.value = v;
                                    break;
                                case "$" :
                                    item[Object.keys(v)[0]] = Object.values(v)[0];
                                    break;
                                default :
                                    item[k] = v;
                            }
                        }
                        obj.AlternateServiceName.push(item);
                    })

                    break;
                case "OtherAffiliation" :
                    obj.OtherAffiliation = Array.isArray(value) ? value : [value]
                default :
                    obj[key] = value;
            }
        }
        return obj;
    })
    let obj = {
        totalMatches: parseInt(json.TotalMatches),
        pageNumber: parseInt(json.PageNumber),
        pageSize: parseInt(json.CurrentSize),
        results: results,
    }
    return obj;
}